/**
 * Client entry point.
 *
 * Wires the milestone-1.5 mobile foundations:
 *   - Quality preset detection (auto -> low/medium/high) feeding the renderer.
 *   - `ShipInputSource` chosen at boot based on device hints (touch vs keyboard).
 *     Both controllers implement the same shared interface, so the rest of the
 *     code never branches on input type.
 *   - `FrameLoop` decoupling sim and render rates and pausing while hidden.
 *   - HUD overlay with responsive CSS reflowing for portrait / narrow landscape.
 *   - Service worker registration for offline shell.
 *
 * Real simulator + network client land in milestones 2-5; this file logs the
 * sampled `ShipInput` once per second so the input plumbing can be smoke-tested
 * end-to-end without them.
 */

import { constants } from "@squid/shared";
import * as THREE from "three";

import { FrameLoop } from "./game/FrameLoop.js";
import { KeyboardMouseController } from "./input/KeyboardMouseController.js";
import { TouchController } from "./input/TouchController.js";
import { registerServiceWorker } from "./pwa/registerServiceWorker.js";
import {
	detectQualityLevel,
	resolveQualityProfile,
} from "./render/QualityPresets.js";
import "./styles/hud.css";
import "./styles/touch-controls.css";
import { isTouchPrimary } from "./util/device.js";
import { HUD } from "./ui/HUD.js";
import { buildTouchControls } from "./ui/TouchControlsOverlay.js";
import type { ShipInputSource } from "@squid/shared";

const container = document.getElementById("app");
if (!container) throw new Error("Missing #app container in index.html");

const qualityLevel = detectQualityLevel();
const quality = resolveQualityProfile(qualityLevel);
console.info("[squid/client] quality", { qualityLevel, quality });

const renderer = new THREE.WebGLRenderer({
	antialias: quality.antialias !== "off",
	alpha: false,
	powerPreference: "high-performance",
});
renderer.setPixelRatio(
	Math.min(window.devicePixelRatio, quality.devicePixelRatioCap),
);
const scaledWidth = Math.max(1, Math.round(window.innerWidth * quality.renderScale));
const scaledHeight = Math.max(1, Math.round(window.innerHeight * quality.renderScale));
renderer.setSize(scaledWidth, scaledHeight, false);
renderer.domElement.style.width = "100%";
renderer.domElement.style.height = "100%";
renderer.setClearColor(0x04060c, 1);
container.appendChild(renderer.domElement);

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(
	60,
	window.innerWidth / window.innerHeight,
	0.1,
	1000,
);
camera.position.set(0, 0, 6);

const placeholder = new THREE.Mesh(
	new THREE.IcosahedronGeometry(1.2, 2),
	new THREE.MeshStandardMaterial({
		color: 0x4da8ff,
		emissive: 0x0a3a6a,
		roughness: 0.4,
		metalness: 0.1,
	}),
);
scene.add(placeholder);
scene.add(new THREE.AmbientLight(0x224466, 0.6));
const keyLight = new THREE.DirectionalLight(0x9bd8ff, 1.1);
keyLight.position.set(3, 4, 5);
scene.add(keyLight);

window.addEventListener("resize", () => {
	camera.aspect = window.innerWidth / window.innerHeight;
	camera.updateProjectionMatrix();
	renderer.setSize(
		Math.round(window.innerWidth * quality.renderScale),
		Math.round(window.innerHeight * quality.renderScale),
		false,
	);
});

const hud = new HUD(document.body);
hud.setConnectionState("connecting");
hud.setFuel(1);

let inputSource: ShipInputSource;
if (isTouchPrimary()) {
	document.body.classList.remove("input-keyboard");
	document.body.classList.add("input-touch");
	const overlay = buildTouchControls(document.body);
	inputSource = new TouchController(overlay.elements);
} else {
	document.body.classList.add("input-keyboard");
	inputSource = new KeyboardMouseController();
}

const probe = inputSource.sample();
console.info("[squid/client] input source ready", {
	touch: isTouchPrimary(),
	sample: probe,
});

let elapsed = 0;
let logTimer = 0;
const loop = new FrameLoop(
	{
		step: (dt) => {
			elapsed += dt;
			const input = inputSource.sample();
			logTimer += dt;
			if (logTimer >= 1) {
				logTimer = 0;
				console.debug("[squid/client] input sample", input);
			}
			void input;
		},
		render: () => {
			placeholder.rotation.y = elapsed * 0.4;
			placeholder.rotation.x = Math.sin(elapsed * 0.6) * 0.2;
			renderer.render(scene, camera);
		},
		onResume: () => {
			console.info("[squid/client] resumed from background");
			hud.setConnectionState("reconnecting");
			// Real reconnect happens via NetClient/Reconnector.kick() once the
			// network milestone lands.
		},
	},
	{ simHz: constants.CLIENT_PREDICT_HZ, keepaliveHz: 10 },
);
loop.start();

void registerServiceWorker();
